import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, ArrowLeft, Mail, Globe, Folder, Server, 
  Clock, Award, Users, Play, RotateCcw, Search,
  Filter, ChevronRight, Star, Trophy, Target, Lock, Monitor, UserCheck
} from 'lucide-react';

const SimulationsList = ({ apiService, userProfile, gamificationEngine }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [userProgress, setUserProgress] = useState({});

  const getStartButtonClasses = (simulation, hasProgress) => {
    if (hasProgress) {
      return 'bg-yellow-600 hover:bg-yellow-500 text-white';
    }
    const colorMap = {
      blue: 'bg-blue-600 hover:bg-blue-500 text-white',
      green: 'bg-green-600 hover:bg-green-500 text-white',
      yellow: 'bg-yellow-600 hover:bg-yellow-500 text-white',
      purple: 'bg-purple-600 hover:bg-purple-500 text-white',
      indigo: 'bg-indigo-600 hover:bg-indigo-500 text-white',
      cyan: 'bg-cyan-600 hover:bg-cyan-500 text-white',
      orange: 'bg-orange-600 hover:bg-orange-500 text-white'
    };
    const colorKey = simulation.iconColor.split('-')[1];
    return colorMap[colorKey] || 'bg-blue-600 hover:bg-blue-500 text-white';
  };

  const simulations = [
    {
      id: 'phishing',
      title: 'Email Security',
      description: 'Identify phishing attempts and email-based threats in realistic scenarios',
      icon: Mail,
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-500/20',
      borderColor: 'hover:border-blue-500',
      difficulty: 'beginner',
      category: 'email',
      estimatedTime: '5-10 min',
      xpRange: '50-300 XP',
      features: ['Real phishing examples', 'Interactive email client', 'Instant feedback'],
      scenarios: 12,
      completionRate: 85
    },
    {
      id: 'web-security', 
      title: 'Web Security',
      description: 'Detect malicious websites, suspicious downloads, and browser-based attacks',
      icon: Globe,
      iconColor: 'text-green-500',
      bgColor: 'bg-green-500/20',
      borderColor: 'hover:border-green-500',
      difficulty: 'intermediate',
      category: 'web',
      estimatedTime: '8-12 min',
      xpRange: '100-350 XP',
      features: ['Browser simulation', 'Download scanning', 'URL analysis'],
      scenarios: 15,
      completionRate: 72
    },
    {
      id: 'file-system-security',
      title: 'File System Security', 
      description: 'Identify suspicious files, analyze permissions, and detect malware signatures',
      icon: Folder,
      iconColor: 'text-yellow-500',
      bgColor: 'bg-yellow-500/20',
      borderColor: 'hover:border-yellow-500',
      difficulty: 'intermediate',
      category: 'filesystem',
      estimatedTime: '5-10 min',
      xpRange: '100-300 XP',
      features: ['OS simulation', 'File analysis', 'Permission checking'],
      scenarios: 10,
      completionRate: 78
    },
    {
      id: 'network-security',
      title: 'Network Security',
      description: 'Analyze network traffic, detect intrusions, and identify suspicious connections', 
      icon: Server,
      iconColor: 'text-purple-500',
      bgColor: 'bg-purple-500/20',
      borderColor: 'hover:border-purple-500',
      difficulty: 'advanced',
      category: 'network',
      estimatedTime: '10-15 min',
      xpRange: '150-400 XP',
      features: ['Traffic analysis', 'Intrusion detection', 'Network monitoring'],
      scenarios: 8,
      completionRate: 64
    },
    {
      id: 'password-security',
      title: 'Password Security',
      description: 'Master password creation, management, and authentication best practices',
      icon: Lock,
      iconColor: 'text-indigo-500',
      bgColor: 'bg-indigo-500/20',
      borderColor: 'hover:border-indigo-500',
      difficulty: 'beginner',
      category: 'authentication',
      estimatedTime: '6-8 min',
      xpRange: '50-250 XP',
      features: ['Password strength analysis', 'MFA scenarios', 'Account security'],
      scenarios: 9,
      completionRate: 88
    },
    {
      id: 'os-security',
      title: 'OS Security',
      description: 'Navigate operating system security settings, permissions, and system-level threats',
      icon: Monitor,
      iconColor: 'text-cyan-500',
      bgColor: 'bg-cyan-500/20',
      borderColor: 'hover:border-cyan-500',
      difficulty: 'intermediate',
      category: 'system',
      estimatedTime: '8-12 min',
      xpRange: '100-350 XP',
      features: ['Permission management', 'System configuration', 'Access controls'],
      scenarios: 11,
      completionRate: 71
    },
    {
      id: 'pretexting',
      title: 'Pretexting Simulation',
      description: 'Recognize and respond to social engineering attacks and manipulation techniques',
      icon: UserCheck,
      iconColor: 'text-orange-500',
      bgColor: 'bg-orange-500/20',
      borderColor: 'hover:border-orange-500',
      difficulty: 'intermediate',
      category: 'social',
      estimatedTime: '10-15 min',
      xpRange: '120-380 XP',
      features: ['Social engineering scenarios', 'Caller ID verification', 'Information protection'],
      scenarios: 7,
      completionRate: 69
    }
  ];

  useEffect(() => {
    // Simulate loading user progress data
    // In a real app, this would fetch from the API
    setUserProgress({
      'phishing': { completed: 8, total: 12, lastPlayed: '2024-01-15', bestScore: 85 },
      'web-security': { completed: 5, total: 15, lastPlayed: '2024-01-10', bestScore: 92 },
      'file-system-security': { completed: 3, total: 10, lastPlayed: '2024-01-12', bestScore: 76 },
      'network-security': { completed: 1, total: 8, lastPlayed: '2024-01-08', bestScore: 68 },
      'password-security': { completed: 6, total: 9, lastPlayed: '2024-01-14', bestScore: 94 },
      'os-security': { completed: 4, total: 11, lastPlayed: '2024-01-11', bestScore: 82 },
      'pretexting': { completed: 2, total: 7, lastPlayed: '2024-01-09', bestScore: 73 }
    });
  }, []);

  const handleStartSimulation = (simulationId) => {
    navigate(`/simulation/${simulationId}`);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-900/50 text-green-300';
      case 'intermediate': return 'bg-yellow-900/50 text-yellow-300';
      case 'advanced': return 'bg-red-900/50 text-red-300';
      default: return 'bg-gradient-to-br from-[#102c4a] to-[#164f3f]/50 text-gray-300';
    }
  };

  const filteredSimulations = simulations.filter(sim => {
    const matchesSearch = sim.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sim.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDifficulty = selectedDifficulty === 'all' || sim.difficulty === selectedDifficulty;
    const matchesCategory = selectedCategory === 'all' || sim.category === selectedCategory;
    
    return matchesSearch && matchesDifficulty && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#102c4a] to-[#164f3f] text-white p-3 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start sm:items-center mb-4">
            <button
              onClick={() => navigate('/challenges')}
              className="p-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.1)] transition-colors mr-4"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2 flex items-center">
                <Shield className="mr-3 text-blue-500" size={28} />
                Security Simulations
              </h1>
              <p className="text-gray-400">
                Practice cybersecurity skills in realistic, interactive environments
              </p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Simulations</p>
                <p className="text-2xl font-bold">{simulations.length}</p>
              </div>
              <Target className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Completed</p>
                <p className="text-2xl font-bold text-green-400">
                  {Object.values(userProgress).reduce((acc, prog) => acc + prog.completed, 0)}
                </p>
              </div>
              <Trophy className="text-green-500" size={24} />
            </div>
          </div>

          <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Average Score</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {Math.round(Object.values(userProgress).reduce((acc, prog) => acc + prog.bestScore, 0) / Object.values(userProgress).length)}%
                </p>
              </div>
              <Star className="text-yellow-500" size={24} />
            </div>
          </div>

          <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Scenarios</p>
                <p className="text-2xl font-bold text-purple-400">
                  {simulations.reduce((acc, sim) => acc + sim.scenarios, 0)}
                </p>
              </div>
              <Users className="text-purple-500" size={24} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search simulations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Difficulty Filter */}
          <div className="flex items-center space-x-2 w-full md:w-auto">
            <Filter size={16} className="text-gray-400" />
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="w-full md:w-auto px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full md:w-auto px-3 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Categories</option>
            <option value="email">Email Security</option>
            <option value="web">Web Security</option>
            <option value="filesystem">File System</option>
            <option value="network">Network Security</option>
            <option value="authentication">Password Security</option>
            <option value="system">OS Security</option>
            <option value="social">Social Engineering</option>
          </select>
        </div>

        {/* Simulations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {filteredSimulations.map((simulation) => {
            const progress = userProgress[simulation.id] || { completed: 0, total: simulation.scenarios, bestScore: 0 };
            const progressPercentage = Math.round((progress.completed / progress.total) * 100);
            const Icon = simulation.icon;

            return (
              <div
                key={simulation.id}
                className={`bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg ${simulation.borderColor} transition-colors overflow-hidden`}
              >
                {/* Header */}
                <div className="p-4 sm:p-6 border-b border-[rgba(255,255,255,0.1)]">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 ${simulation.bgColor} rounded-lg`}>
                      <Icon size={24} className={simulation.iconColor} />
                    </div>
                    <div className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(simulation.difficulty)}`}>
                      {simulation.difficulty}
                    </div>
                  </div>

                  <h3 className="text-lg sm:text-xl font-bold mb-2">{simulation.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{simulation.description}</p>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Progress: {progress.completed}/{progress.total} scenarios</span>
                      <span>{progressPercentage}%</span>
                    </div>
                    <div className="w-full bg-[rgba(255,255,255,0.1)] rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${simulation.iconColor.replace('text-', 'bg-')}`}
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center text-xs">
                    <div>
                      <div className="text-gray-400">Time</div>
                      <div className="font-medium">{simulation.estimatedTime}</div>
                    </div>
                    <div>
                      <div className="text-gray-400">XP Range</div>
                      <div className="font-medium">{simulation.xpRange}</div>
                    </div>
                    <div>
                      <div className="text-gray-400">Best Score</div>
                      <div className="font-medium">{progress.bestScore}%</div>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="p-4 border-b border-[rgba(255,255,255,0.1)]">
                  <div className="text-xs text-gray-400 mb-2">Features:</div>
                  <div className="flex flex-wrap gap-1">
                    {simulation.features.map((feature, index) => (
                      <span
                        key={index}
                        className="text-xs bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="p-4 flex flex-col sm:flex-row gap-3 sm:gap-2 sm:justify-between sm:items-center">
                  <div className="text-xs text-gray-500">
                    Completion Rate: {simulation.completionRate}%
                  </div>
                  <button
                    onClick={() => handleStartSimulation(simulation.id)}
                    className={`w-full sm:w-auto px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${getStartButtonClasses(simulation, progress.completed > 0)}`}
                  >
                    {progress.completed > 0 ? (
                      <>
                        <RotateCcw size={16} />
                        <span>Continue</span>
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        <span>Start</span>
                      </>
                    )}
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* No Results */}
        {filteredSimulations.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">
              <Search size={48} className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-gray-400 mb-2">No simulations found</h3>
            <p className="text-gray-500">Try adjusting your search terms or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimulationsList;